import { yupResolver } from "@hookform/resolvers/yup";
import { Close as CloseIcon } from "@mui/icons-material";
import { Dialog, DialogContent, IconButton, Slide, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import * as yup from "yup";
import "yup-phone-lite";

import { EXPERIENCE_STATUS, SHARE_SCOPE, TAB_STATUSES } from "@/constants/index.js";
import {
  getCurrentShare,
  getCurrentSharedExperience,
  resetCurrentShare,
  resetCurrentSharedExperience,
  setSimpleValue,
} from "@/store/actions";
import { selectUser } from "@/store/auth/auth.slice";
import { useThunkDispatch, useThunkSelector } from "@/store/hooks";
import {
  currentMediaItemSelector,
  currentPropertySelector,
  currentShareSelector,
  currentSharedExperienceSelector,
  visualBuilderDataSelector,
} from "@/store/selectors";
import { addMediaShare, addShare, updateMediaShare, updateShare } from "@/utils/shareUtils";
import { isPublicPage } from "@/utils/visualBuilder";

import { useUrlParams } from "../../../hooks";
import { ExperienceService } from "../../../services";
import { validateExperienceTitle } from "../../../utils/validator";
import { changeTabStatus } from "../MainHeader/MainHeaderDataSlice";
import ShareButtons from "./components/ShareButtons";
import ShareContactField from "./components/ShareContactField";
import ShareMessageField from "./components/ShareMessageField";
import ShareQrCode from "./components/ShareQrCode";
import ShareTitleField from "./components/ShareTitleField";
import styles from "./styles-share-dialog";

const useStyles = makeStyles(styles);

const shareFormSchema = yup
  .object({
    title: yup
      .string()
      .required("Required")
      .test("pattern", "Invalid", (value) => validateExperienceTitle(value)),
    email: yup.string().lowercase().email("Invalid email").required("Email is required."),
    phone: yup.string().phone(["US"], "Invalid phone").required("Phone is required."),
    contactField: yup.string().when((value, schema, options) => {
      const { contactId } = options.parent;

      if (!contactId) {
        if (/^[0-9+]+$/.test(value)) {
          return schema.phone(["US"], "Invalid phone").required("Required");
        } else {
          return schema.email("Invalid email").required("Required");
        }
      }

      return schema.required("Required");
    }),
    contactId: yup.number().optional().nullable().typeError("Contact id is invalid"),
    message: yup.string().optional().nullable(),
  })
  .required();

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

// interface ShareDialogProps {
//   tabStatus: proptypes.string,
// };

export default function ShareDialog(props) {
  const { tabStatus, shareContext } = props;
  const initialShareIdRef = useRef(null);

  const classes = useStyles();
  const dispatch = useThunkDispatch();

  const headerRef = useRef();
  const centerRef = useRef();

  const params = useUrlParams();
  const { organizationId } = params;

  const { visualBuilderData } = useSelector(visualBuilderDataSelector(params));
  const { primaryFilter, typeFilterId, tagFilterId, leftDrawer, rightDrawer, gridView, currentMediaItemId } =
    visualBuilderData;

  const { share } = useSelector(currentShareSelector);

  const {
    shortUrl: shareShortUrl = "",
    url,
    options: { isSingleAsset = false, scope: shareScope, isSingleExperience = false } = {},
  } = share;

  const shareUrl = shareShortUrl || url;

  const user = useThunkSelector(selectUser);
  const { property } = useSelector(currentPropertySelector);
  const { experience } = useSelector(currentSharedExperienceSelector);
  const { mediaItem } = useSelector(currentMediaItemSelector);

  const { id: userId } = user;
  const { id: propertyId } = property;
  const { id: experienceId, isTemplate } = experience;

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [activeCount, setActiveCount] = useState(0);
  const [justReset, setJustReset] = useState(false);

  const [isShareLinkLoading, setIsShareLinkLoading] = useState(true);

  const shareOptions = useMemo(
    () => ({
      leftDrawer,
      rightDrawer,
      scope: shareScope || SHARE_SCOPE.EXPERIENCE,
      ...(currentMediaItemId && { currentMediaItemId }),
      ...(isSingleExperience && { isSingleExperience }),
    }),
    [leftDrawer, rightDrawer, shareScope, currentMediaItemId, isSingleExperience],
  );

  const {
    control,
    setValue,
    getValues,
    reset,
    formState: { errors },
    trigger,
  } = useForm({
    mode: "all",
    resolver: yupResolver(shareFormSchema),
    defaultValues: {
      title: "",
      message: "",
      phone: "",
      email: "",
      contactId: null,
      contactField: "",
    },
    context: {},
  });

  const values = getValues();

  // Set default shareId, that going to be used when the contact are being removed
  useEffect(() => {
    if (!initialShareIdRef.current && share?.id) {
      initialShareIdRef.current = share.id;

      setIsShareLinkLoading(false);
    }
  }, [share?.id]);

  useEffect(() => {
    if (!experienceId) {
      setValue("title", isSingleAsset ? mediaItem.name : `${property.name} - Full Collection`, {
        shouldValidate: true,
        shouldTouch: true,
        shouldDirty: true,
      });
      return;
    }
    setValue("title", experience.title, { shouldValidate: true, shouldTouch: true, shouldDirty: true });
  }, [experienceId, isSingleAsset, mediaItem.name, property.name, experience.title, setValue]);

  useEffect(() => {
    // Replacing message after reseting share
    if (justReset && share.id === initialShareIdRef.current) {
      setValue("message", share.message ?? "", {
        shouldTouch: true,
        shouldDirty: true,
      });
      setJustReset(false);
    }
  }, [justReset, share.message, initialShareIdRef.current, share.id]);

  useEffect(() => {
    trigger().then();
  }, []);

  useEffect(() => {
    if (share?.id && shareUrl) {
      setIsShareLinkLoading(false);
    }
  }, [share.id, shareUrl]);

  const buildShareValues = (data, contactId) => {
    const values = {
      title: data.title,
      ...(data.message && { message: data.message }),
      ...(contactId && { contactId }),
      ...(data.email && { email: data.email }),
      ...(data.phone && { phone: data.phone.startsWith("+") ? data.phone : `+1${data.phone}` }),
    };
    return values;
  };

  const handleContactChange = async (contactId) => {
    if (isResetToInitialShare(contactId)) {
      handleResetToInitialShare();
      return;
    }

    const data = getValues();

    if (isSameContactInfo(contactId, data)) return;

    const values = buildShareValues(data, contactId);
    const newShareOptions = buildShareOptions();

    if (contactId) {
      await handleCreateNewShare(values, newShareOptions);
    } else {
      await handleUpdateExistingShare(data, values);
    }
  };

  const isResetToInitialShare = (contactId) => !contactId && share.contactId;

  const handleResetToInitialShare = () => {
    setIsShareLinkLoading(true);
    setJustReset(true);
    setTimeout(() => {
      dispatch(getCurrentShare(propertyId, experienceId, initialShareIdRef.current, userId));
    }, 0);
  };

  const isSameContactInfo = (contactId, data) => {
    const sameContact = contactId && contactId === share.contactId;
    const sameEmail = data.email && data.email === share.email;
    const samePhone = data.phone && data.phone === share.phone;

    return (
      (contactId && sameContact) || (!contactId && data.email && sameEmail) || (!contactId && data.phone && samePhone)
    );
  };

  const buildShareOptions = () => {
    let options = { ...shareOptions };
    if (isPublicPage(tabStatus)) {
      options = {
        ...options,
        gridView,
        ...(primaryFilter && { primaryFilter }),
        ...(typeFilterId && { typeFilterId }),
        ...(tagFilterId && { tagFilterId }),
        ...(currentMediaItemId && { currentMediaItemId }),
      };
    }
    return options;
  };

  const handleCreateNewShare = async (values, options) => {
    setIsShareLinkLoading(true);

    const resp = experienceId
      ? await addShare(propertyId, experienceId, userId, values, options)
      : await addMediaShare(
          propertyId,
          userId,
          { ...values, ...(shareContext === "single-asset" ? { mediaItemId: currentMediaItemId } : {}) },
          options,
        );

    if (!resp.err) {
      dispatch(getCurrentShare(propertyId, experienceId, resp.shareId, userId));
    }
  };

  const handleUpdateExistingShare = async (data, values) => {
    values.email = share.email !== data.email ? data.email || null : values.email;
    if (share.phone !== data.phone) {
      if (data.phone) {
        values.phone = data.phone.startsWith("+") ? data.phone : `+1${data.phone}`;
      } else {
        values.phone = null;
      }
    }
    values.message = share.message !== data.message ? data.message || null : values.message;

    if (experienceId) {
      await updateShare(propertyId, experienceId, share.id, userId, values);
    } else {
      await updateMediaShare(propertyId, share.id, userId, values);
    }
    dispatch(getCurrentShare(propertyId, experienceId, share.id, userId));
  };

  const handleTitleChange = async (title) => {
    if (title) {
      await updateShare(propertyId, experienceId, share.id, userId, {
        title,
      });

      dispatch(getCurrentSharedExperience(propertyId, experienceId, userId));
      dispatch(getCurrentShare(propertyId, experienceId, share.id, userId));
    }
  };

  const handleMessageChange = async (message) => {
    const newMessage = message || null;
    if (experienceId) {
      await updateShare(propertyId, experienceId, share.id, userId, {
        message: newMessage,
      });
    } else {
      await updateMediaShare(propertyId, share.id, userId, {
        message: newMessage,
      });
    }

    dispatch(getCurrentShare(propertyId, experienceId, share.id, userId));
  };

  const onCloseDialog = async () => {
    dispatch(setSimpleValue("share_without_experience", false));
    dispatch(setSimpleValue("share_experience", false));

    setSnackbarOpen(false);
    reset();
    setJustReset(true);
    dispatch(resetCurrentSharedExperience());
    dispatch(resetCurrentShare());
    if (tabStatus === TAB_STATUSES.HOMEBASE) {
      dispatch(changeTabStatus({ tab: "property-tab" }));
    }
  };

  const updateExperienceStatus = async (status) => {
    await ExperienceService.updateExperience(propertyId, experienceId, {
      status,
    });
  };

  const experienceRoutineOnShare = async () => {
    // Ensure there's an experienceId, userId, and the status isn't TEMPLATE.
    if (!experienceId || !userId || isTemplate) {
      return;
    }

    // If there is a title and the title isn't "Untitled" change to SHARED, if needed.
    // Otherwise change to DRAFT, if needed.
    if (experience.title && experience.title !== "Untitled") {
      // Run only if status isn't already SHARED.
      if (experience.status !== EXPERIENCE_STATUS.SHARED) return await updateExperienceStatus(EXPERIENCE_STATUS.SHARED);
    } else if (experience.status !== EXPERIENCE_STATUS.DRAFT)
      return await updateExperienceStatus(EXPERIENCE_STATUS.DRAFT);
  };

  const onContentClick = (e) => {
    if (e.target.id && e.target.id.indexOf("menu-item") > -1) {
      return;
    }
    if (
      e.target.className &&
      typeof e.target.className === "string" &&
      e.target.className.indexOf("MuiBackdrop") > -1
    ) {
      return;
    }
    if (e.button !== 2 && !headerRef.current.contains(e.target) && !centerRef.current.contains(e.target)) {
      const listener = async () => {
        await onCloseDialog();
      };
      e.target.addEventListener("mouseup", listener);
    }
  };

  const handleFocus = () => setActiveCount((c) => c + 1);
  const handleBlur = () => setActiveCount((c) => Math.max(0, c - 1));

  const isAnyFieldActive = activeCount > 0;

  return (
    <Dialog
      className={classes.modalDialog}
      open
      fullScreen
      fullWidth
      onClose={onCloseDialog}
      aria-labelledby="form-dialog-title"
      TransitionComponent={Transition}
      maxWidth="md"
    >
      <div style={{ height: "100%" }}>
        <DialogContent className={classes.modalPopup} onMouseDown={onContentClick}>
          <div className={classes.popupContent} ref={centerRef}>
            <div className={classes.popupHeader} ref={headerRef}>
              <div className={classes.popupHeaderInfo}>
                <Typography className={classes.popupHeaderText}>Share</Typography>
              </div>
              <span className={classes.popupClose}>
                <IconButton size="small" className={classes.popupClose} onClick={onCloseDialog}>
                  <CloseIcon />
                </IconButton>
              </span>
            </div>
            <div className={classes.popupMainContent}>
              <div className={classes.popupLeftSide}>
                <ShareTitleField
                  classes={classes}
                  control={control}
                  errors={errors}
                  getValues={getValues}
                  propertyId={propertyId}
                  experienceId={experienceId}
                  experience={experience}
                  tabStatus={tabStatus}
                  shareOptions={shareOptions}
                  handleShareChange={handleTitleChange}
                  handleFocus={handleFocus}
                  handleBlur={handleBlur}
                />
                <ShareContactField
                  classes={classes}
                  propertyId={propertyId}
                  control={control}
                  values={values}
                  setValue={setValue}
                  trigger={trigger}
                  tabStatus={tabStatus}
                  organizationId={organizationId}
                  handleContactChange={handleContactChange}
                  handleFocus={handleFocus}
                  handleBlur={handleBlur}
                />
                <ShareMessageField
                  classes={classes}
                  control={control}
                  handleShareChange={handleMessageChange}
                  handleFocus={handleFocus}
                  handleBlur={handleBlur}
                />
                <ShareButtons
                  classes={classes}
                  share={share}
                  isShareLinkLoading={isShareLinkLoading}
                  snackbarOpen={snackbarOpen}
                  setSnackbarOpen={setSnackbarOpen}
                  propertyId={propertyId}
                  experienceId={experienceId}
                  userId={userId}
                  experienceRoutineOnShare={experienceRoutineOnShare}
                  onCloseDialog={onCloseDialog}
                  errors={errors}
                  experience={experience}
                  shareUrl={shareUrl}
                  disabled={isAnyFieldActive || isShareLinkLoading}
                />
              </div>
              <div className={classes.popupRightSide}>
                <ShareQrCode classes={classes} isShareLinkLoading={isShareLinkLoading} shareUrl={shareUrl} />
              </div>
            </div>
          </div>
        </DialogContent>
      </div>
    </Dialog>
  );
}
